// The Pricing tab (spec/13 §18, spec/06 §4; F-PRC-10): pay-as-you-go's one home (2026-10-07) — its on/off switch, the
// rate and the session money settings around it, read as one pricing card rather than a settings form. Settings →
// Time & pricing shows a one-line summary that links here.
//
// The values are the settings registry's (`apps/server/src/modules/sessions/settings.ts`), read and
// written through the same settings api the Settings screen uses — `GET/PUT …/settings` with its ETag —
// at the venue or the organisation this page is about:
//
//   sessions.payAsYouGoEnabled       whether players pay for play by the hour at all (the switch on the card)
//   sessions.paygHourlyRate          the price of an hour of play (money, integer paise)
//   sessions.paygBillingUnitSeconds  the step play is billed in: a started step is charged in full
//   sessions.paygDefaultBudget       what a player may spend at the kiosk before choosing how to pay
//   sessions.walletFundsPlay         whether a wallet pays for play at the rate
//
// The switch saves at once (a switch takes effect immediately), turning off only after saying what it does to players;
// "Edit pricing" opens all five in the settings renderer (`SettingsForm`), in a dialog, so a money value is typed in
// rupees, the rate and the rest collapse under the switch while it is off, a 412 keeps the editor's work, and who may
// change what is the server's answer. There is one rate for every station class today: rate cards per class, time-of-day bands and
// daily caps are F-PRC-10/-11/-12, and the card says so rather than inventing an api for them.

import type { SettingEntry, SettingsView } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Dialog, Skeleton, Switch, useToast } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';

import { useApi } from '../../api/client';
import type { Versioned } from '../../api/versioned';
import { LoadFailure } from '../../shell/LoadFailure';
import { useConsoleScope } from '../../shell/scope';
import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { useRegistryLabels, useSettingHelp } from '../../settings/labels';
import { settingMoney } from '../../settings/money';
import { readSettings, registryFieldKey, toRenderable, writeSettings, type RenderOptions, type SettingsScope } from '../../settings/registry-source';
import { SettingsForm } from '../../settings/SettingsForm';
import { settingsKey, useSettingsView } from '../../settings/SettingsPage';
import type { SettingsPart } from '../../settings/types';
import { Notice } from '../../ui';
import { useAccess } from '../../api/queries';
import { RateCardSection } from './RateCardSection';

const PRICING_MANAGE = 'pricing:manage';

/** The settings this card is drawn from, in the order the dialog shows them. */
export const PAYG_KEYS = [
  'sessions.payAsYouGoEnabled',
  'sessions.paygHourlyRate',
  'sessions.paygBillingUnitSeconds',
  'sessions.paygDefaultBudget',
  'sessions.walletFundsPlay',
] as const;

const PAYG_SWITCH = 'sessions.payAsYouGoEnabled';

export function PaygPricingTab(): ReactNode {
  const { t } = useTranslation('console');
  const consoleScope = useConsoleScope();
  const scope = useMemo<SettingsScope>(
    () =>
      consoleScope.kind === 'venue'
        ? { kind: 'venue', organizationId: consoleScope.venue.organizationId, venueId: consoleScope.venue.id }
        : { kind: 'organization', organizationId: consoleScope.organization.id },
    [consoleScope],
  );
  const { query } = useSettingsView(scope);
  const access = useAccess(consoleScope.kind === 'venue' ? { venueId: consoleScope.venue.id } : undefined);
  const canManageCard = access.status === 'ready' && access.can(PRICING_MANAGE);
  const [editing, setEditing] = useState(false);
  // Wallets paying for play needs wallets on, which the organisation sets: read from its screen at a venue.
  const organizationScope = useMemo<SettingsScope>(
    () => ({ kind: 'organization', organizationId: consoleScope.kind === 'venue' ? consoleScope.venue.organizationId : consoleScope.organization.id }),
    [consoleScope],
  );
  const organizationView = useSettingsView(organizationScope, consoleScope.kind === 'venue').query.data?.data;

  if (query.data === undefined) {
    return (
      <div className="console-pricing-panel">
        {query.isError ? <LoadFailure error={query.error} message={t('pricing.payg.unavailable')} onRetry={() => void query.refetch()} /> : <Skeleton height="14rem" />}
      </div>
    );
  }

  const view = query.data.data;
  const entries = new Map(view.settings.filter((entry) => (PAYG_KEYS as readonly string[]).includes(entry.key)).map((entry) => [entry.key, entry]));
  const rate = entries.get('sessions.paygHourlyRate');
  if (rate === undefined) {
    return (
      <div className="console-pricing-panel">
        <Notice role="note">{t('pricing.payg.notHere')}</Notice>
      </div>
    );
  }
  const editable = [...entries.values()].some((entry) => entry.editable);

  return (
    <div className="console-pricing-panel">
      <PaygCard
        view={view}
        entries={entries}
        canEdit={editable}
        onEdit={() => setEditing(true)}
        atVenue={consoleScope.kind === 'venue'}
        switcher={<PaygSwitch scope={scope} view={query.data} entry={entries.get(PAYG_SWITCH)} />}
      />
      {/* The venue's rate card (F-PRC-10 … F-PRC-12): per class, with bands and a minimum. It is per venue, so the
          organisation's page says where to find it. */}
      {consoleScope.kind === 'venue' ? (
        <RateCardSection organizationId={consoleScope.venue.organizationId} venueId={consoleScope.venue.id} canManage={canManageCard} />
      ) : (
        <Notice role="note" title={t('pricing.payg.perClassTitle')}>
          {t('pricing.payg.perClass')}
        </Notice>
      )}
      <Dialog open={editing} onOpenChange={setEditing} size="md" title={t('pricing.payg.editTitle')} description={t('pricing.payg.editHint')}>
        {editing ? <PaygEditor scope={scope} view={query.data} walletEnabled={walletEnabled(view, organizationView)} /> : null}
      </Dialog>
    </div>
  );
}

// -------------------------------------------------------------------------------------------------
// The card
// -------------------------------------------------------------------------------------------------

/** Whether wallets are on for the organisation: on its own screen, or read from it at a venue. */
function walletEnabled(view: SettingsView, organizationView: SettingsView | undefined): unknown {
  return (view.settings.find((entry) => entry.key === 'wallet.enabled') ?? organizationView?.settings.find((entry) => entry.key === 'wallet.enabled'))?.value;
}

function PaygCard({
  view,
  entries,
  canEdit,
  onEdit,
  atVenue,
  switcher,
}: {
  readonly view: SettingsView;
  readonly entries: ReadonlyMap<string, SettingEntry>;
  readonly canEdit: boolean;
  readonly onEdit: () => void;
  readonly atVenue: boolean;
  readonly switcher: ReactNode;
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const currency = view.scope.currency ?? 'INR';
  const rate = entries.get('sessions.paygHourlyRate');
  const unit = entries.get('sessions.paygBillingUnitSeconds');
  const budget = entries.get('sessions.paygDefaultBudget');
  const wallet = entries.get('sessions.walletFundsPlay');
  const money = (entry: SettingEntry | undefined) => (typeof entry?.value === 'number' ? format.money({ amount: entry.value, currency }) : '—');
  const unitSeconds = typeof unit?.value === 'number' ? unit.value : 60;
  const budgetMinor = typeof budget?.value === 'number' ? budget.value : 0;
  const on = entries.get(PAYG_SWITCH)?.value !== false;

  return (
    // On, the card carries the green rail of something in use (ADR-0043); off, a quiet one — the words say which.
    <section className="console-pricing-card console-rail" data-rail={on ? 'ok' : 'quiet'} aria-labelledby="console-pricing-card-title">
      <div className="console-pricing-card__head">
        <div className="console-pricing-card__lede">
          <h2 id="console-pricing-card-title" className="console-pricing-card__title">
            {t('pricing.payg.title')}
          </h2>
          <p className="console-subtle">{atVenue ? t('pricing.payg.lede') : t('pricing.payg.ledeOrganization')}</p>
        </div>
        {canEdit ? (
          <Button variant="primary" onClick={onEdit}>
            {t('pricing.payg.edit')}
          </Button>
        ) : null}
      </div>
      {switcher}
      {on ? null : (
        <p className="console-pricing-off" role="note">
          {t('pricing.payg.offNote')}
        </p>
      )}
      <div className="console-pricing-rate" data-off={on ? undefined : true}>
        <span className="console-pricing-rate__figure clav-figure">{money(rate)}</span>
        <span className="console-pricing-rate__per">{t('pricing.payg.perHour')}</span>
        {rate === undefined ? null : <SourceBadge entry={rate} />}
      </div>
      <dl className="console-pricing-terms">
        <div>
          <dt>{t('pricing.payg.billingStep')}</dt>
          <dd>{t('pricing.payg.step', { count: Math.round(unitSeconds / 60) })}</dd>
          <dd className="console-subtle">{t('pricing.payg.stepHint')}</dd>
        </div>
        <div>
          <dt>{t('pricing.payg.budget')}</dt>
          <dd className="console-num">{budgetMinor === 0 ? t('pricing.payg.budgetNone') : money(budget)}</dd>
          <dd className="console-subtle">{budgetMinor === 0 ? t('pricing.payg.budgetNoneHint') : t('pricing.payg.budgetHint')}</dd>
        </div>
        <div>
          <dt>{t('pricing.payg.wallet')}</dt>
          <dd>{wallet?.value === true ? t('pricing.payg.walletOn') : t('pricing.payg.walletOff')}</dd>
          <dd className="console-subtle">{wallet?.value === true ? t('pricing.payg.walletOnHint') : t('pricing.payg.walletOffHint')}</dd>
        </div>
      </dl>
      {canEdit ? null : <p className="console-subtle">{t('pricing.payg.readOnly')}</p>}
    </section>
  );
}

/** Where the rate comes from, in the Settings screen's words: the default, head office, the organisation or this venue. */
function SourceBadge({ entry }: { readonly entry: SettingEntry }): ReactNode {
  const { t } = useTranslation('console');
  if (entry.lockedAt !== undefined) return <Badge tone="neutral">{t('settings.locked.badge')}</Badge>;
  return <Badge tone={entry.source === 'default' ? 'neutral' : 'info'}>{t(`settings.source.${entry.source}`)}</Badge>;
}

// -------------------------------------------------------------------------------------------------
// The switch: pay-as-you-go on or off, saved at once
// -------------------------------------------------------------------------------------------------

/**
 * `sessions.payAsYouGoEnabled` as the card's switch. Turning it off asks first — players then need a pass or time
 * staff add, and the wallet no longer pays for play — then saves with the screen's ETag, as the editor does; turning
 * it on saves at once. Read-only where the member may not change it, saying so.
 */
function PaygSwitch({ scope, view, entry }: { readonly scope: SettingsScope; readonly view: Versioned<SettingsView>; readonly entry: SettingEntry | undefined }): ReactNode {
  const { t } = useTranslation('console');
  const api = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const problem = useProblem();
  const [asking, setAsking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState<string | undefined>(undefined);
  if (entry === undefined) return null;
  const on = entry.value !== false;

  const save = async (next: boolean) => {
    setSaving(true);
    setFailed(undefined);
    try {
      const fresh = await writeSettings(api, scope, [{ key: PAYG_SWITCH, value: next }], view.etag);
      queryClient.setQueryData(settingsKey(scope), fresh);
      toast.show({ tone: 'ok', title: next ? t('pricing.payg.turnedOn') : t('pricing.payg.turnedOff') });
      setAsking(false);
    } catch (error) {
      // Someone saved since this screen read: read it again, and say so.
      if (problemCode(error) === PROBLEM.staleWrite) void queryClient.invalidateQueries({ queryKey: settingsKey(scope) });
      setFailed(problemCode(error) === PROBLEM.staleWrite ? t('pricing.payg.switchStale') : problem.text(error, { fallback: t('pricing.payg.switchFailed') }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="console-pricing-switch" data-setting={PAYG_SWITCH}>
      {entry.editable ? (
        // The card's title already says "Pay as you go": the switch shows its state in words, and keeps its full name
        // for a screen reader.
        <Switch
          label={
            <>
              <span className="clav-visually-hidden">{`${t('pricing.payg.switchLabel')} `}</span>
              {on ? t('settings.value.on') : t('settings.value.off')}
            </>
          }
          checked={on}
          disabled={saving}
          onCheckedChange={(checked) => (checked ? void save(true) : setAsking(true))}
        />
      ) : (
        <p className="console-pricing-switch__state">
          {t('pricing.payg.switchLabel')}: {on ? t('settings.value.on') : t('settings.value.off')}
        </p>
      )}
      <p className="console-subtle">{on ? t('pricing.payg.switchOnHint') : t('pricing.payg.switchOffHint')}</p>
      {failed === undefined ? null : (
        <p className="clav-field__error" role="alert">
          {failed}
        </p>
      )}
      <Dialog open={asking} onOpenChange={setAsking} title={t('pricing.payg.confirmOffTitle')} description={t('pricing.payg.confirmOffBody')}>
        <div className="console-form__actions console-form__actions--end">
          <Button variant="ghost" onClick={() => setAsking(false)}>
            {t('pricing.payg.keepOn')}
          </Button>
          <Button variant="danger" busy={saving} onClick={() => void save(false)}>
            {t('pricing.payg.turnOff')}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

// -------------------------------------------------------------------------------------------------
// The editor: the settings renderer, over just these five settings
// -------------------------------------------------------------------------------------------------

function PaygEditor({ scope, view, walletEnabled }: { readonly scope: SettingsScope; readonly view: Versioned<SettingsView>; readonly walletEnabled: unknown }): ReactNode {
  const { t } = useTranslation('console');
  const api = useApi();
  const queryClient = useQueryClient();
  const labels = useRegistryLabels();
  const helpOf = useSettingHelp();
  const formatter = useVenueFormatter();
  const key = settingsKey(scope);

  const part = useMemo<SettingsPart>(() => {
    const currency = view.data.scope.currency;
    // Drawn here as rows of their own: the dialog is pay-as-you-go's home (`screen: 'platform'` keeps every row).
    const render: RenderOptions = { screen: 'platform', ...(currency ? { money: settingMoney(formatter, currency) } : {}) };
    const words = {
      label: (settingKey: string) => labels.field(settingKey, []),
      help: helpOf,
      section: (id: string) => t(`settings.section.${id}`, { defaultValue: id }),
    };
    const entriesOf = (next: Versioned<SettingsView>) => ({
      data: PAYG_KEYS.flatMap((wanted) => next.data.settings.filter((entry) => entry.key === wanted)).map((entry) => toRenderable(entry, words, render)),
      etag: next.etag,
    });
    const onVersion = (next: Versioned<SettingsView>) => queryClient.setQueryData(key, next);
    const first = entriesOf(view);
    return {
      id: 'registry',
      entries: first.data,
      etag: first.etag,
      save: async (changes, etag) => {
        const next = await writeSettings(api, scope, changes, etag);
        onVersion(next);
        return entriesOf(next);
      },
      reload: async () => {
        const next = await readSettings(api, scope);
        onVersion(next);
        return entriesOf(next);
      },
      fieldKey: registryFieldKey,
    };
  }, [api, scope, view, labels, helpOf, t, formatter, queryClient, key]);

  return (
    <SettingsForm
      key={key.join('/')}
      parts={[part]}
      labels={labels}
      externalValueOf={(master) => (master === 'wallet.enabled' ? walletEnabled : undefined)}
      externalLabel={(master) => labels.field(master, [])}
    />
  );
}
